import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Search,
  Tv,
  Film,
  Tv2,
  Play,
  Star,
  Zap,
  ArrowRight,
  Clock,
  Sparkles,
  CornerDownLeft,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { Channel, Movie, Series } from '../../types/iptv';
import { SeriesPoster } from './SeriesPoster';

interface QuickSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const QuickSearchModal: React.FC<QuickSearchModalProps> = ({ isOpen, onClose }) => {
  const {
    channels,
    movies,
    series,
    playChannel,
    playMovie,
    playSeriesEpisode,
    parentalConfig,
    isParentalUnlocked,
    openPinModal,
  } = useIPTV();

  const [query, setQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'live' | 'movies' | 'series'>('all');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus on mount
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Global shortcut handler for Esc, Arrows, Enter
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(searchResults.length - 1, prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (selectedIndex === 0 || document.activeElement === inputRef.current) {
          onClose();
        } else {
          setSelectedIndex((prev) => Math.max(0, prev - 1));
        }
      } else if (e.key === 'Backspace' && query === '') {
        onClose();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (searchResults[selectedIndex]) {
          handleSelect(searchResults[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Unified search results
  const searchResults = useMemo(() => {
    if (!query.trim()) {
      // Return top popular items when empty
      const initial: Array<
        | { type: 'live'; item: Channel }
        | { type: 'movie'; item: Movie }
        | { type: 'series'; item: Series }
      > = [];

      if (filterType === 'all' || filterType === 'live') {
        channels.slice(0, 4).forEach((c) => initial.push({ type: 'live', item: c }));
      }
      if (filterType === 'all' || filterType === 'movies') {
        movies.slice(0, 3).forEach((m) => initial.push({ type: 'movie', item: m }));
      }
      if (filterType === 'all' || filterType === 'series') {
        series.slice(0, 3).forEach((s) => initial.push({ type: 'series', item: s }));
      }
      return initial;
    }

    const q = query.toLowerCase().trim();
    const results: Array<
      | { type: 'live'; item: Channel }
      | { type: 'movie'; item: Movie }
      | { type: 'series'; item: Series }
    > = [];

    if (filterType === 'all' || filterType === 'live') {
      channels.forEach((c) => {
        if (c.isAdult && parentalConfig.isLockedByDefault && !isParentalUnlocked) return;
        if (
          c.name.toLowerCase().includes(q) ||
          c.groupTitle.toLowerCase().includes(q) ||
          c.num.toString() === q
        ) {
          results.push({ type: 'live', item: c });
        }
      });
    }

    if (filterType === 'all' || filterType === 'movies') {
      movies.forEach((m) => {
        if (m.isAdult && parentalConfig.isLockedByDefault && !isParentalUnlocked) return;
        if (
          m.title.toLowerCase().includes(q) ||
          m.category.toLowerCase().includes(q) ||
          (m.cast && m.cast.some((actor) => actor.toLowerCase().includes(q)))
        ) {
          results.push({ type: 'movie', item: m });
        }
      });
    }

    if (filterType === 'all' || filterType === 'series') {
      series.forEach((s) => {
        if (s.isAdult && parentalConfig.isLockedByDefault && !isParentalUnlocked) return;
        if (
          s.title.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q) ||
          (s.cast && s.cast.some((actor) => actor.toLowerCase().includes(q)))
        ) {
          results.push({ type: 'series', item: s });
        }
      });
    }

    return results.slice(0, 30);
  }, [query, filterType, channels, movies, series, parentalConfig, isParentalUnlocked]);

  const handleSelect = (entry: { type: 'live'; item: Channel } | { type: 'movie'; item: Movie } | { type: 'series'; item: Series }) => {
    onClose();
    if (entry.type === 'live') {
      const channel = entry.item;
      if (channel.isAdult && parentalConfig.isLockedByDefault && !isParentalUnlocked) {
        openPinModal(`Desbloquear ${channel.name}`, () => playChannel(channel));
      } else {
        playChannel(channel);
      }
    } else if (entry.type === 'movie') {
      const movie = entry.item;
      if (movie.isAdult && parentalConfig.isLockedByDefault && !isParentalUnlocked) {
        openPinModal(`Desbloquear ${movie.title}`, () => playMovie(movie));
      } else {
        playMovie(movie);
      }
    } else if (entry.type === 'series') {
      const s = entry.item;
      if (s.episodes && s.episodes.length > 0) {
        if (s.isAdult && parentalConfig.isLockedByDefault && !isParentalUnlocked) {
          openPinModal(`Desbloquear ${s.title}`, () => playSeriesEpisode(s, s.episodes[0]));
        } else {
          playSeriesEpisode(s, s.episodes[0]);
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-start justify-center pt-16 md:pt-24 p-4 select-none animate-in fade-in duration-150">
      <div className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
        {/* Search Bar Input */}
        <div className="p-4 md:p-5 border-b border-zinc-800/80 flex items-center gap-3 bg-zinc-900/70">
          <Search className="w-5 h-5 text-theme-accent shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Busca instantânea: canais, filmes, séries ou número..."
            className="flex-1 bg-transparent text-sm md:text-base text-white placeholder-zinc-500 focus:outline-none font-medium"
          />
          <div className="hidden sm:flex items-center gap-1 text-[10px] font-mono text-zinc-400 bg-zinc-800/80 px-2 py-1 rounded-lg border border-zinc-700">
            <span>ESC para sair</span>
          </div>
        </div>

        {/* Filter Badges */}
        <div className="px-4 py-2.5 bg-zinc-900/40 border-b border-zinc-800/60 flex items-center gap-2 overflow-x-auto">
          {(['all', 'live', 'movies', 'series'] as const).map((type) => {
            const isSelected = filterType === type;
            const labels = {
              all: 'Tudo',
              live: '📺 Canais Ao Vivo',
              movies: '🎬 Filmes VOD',
              series: '🍿 Séries VOD',
            };
            return (
              <button
                key={type}
                onClick={() => {
                  setFilterType(type);
                  setSelectedIndex(0);
                }}
                className={`px-3 py-1 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap ${
                  isSelected
                    ? 'bg-theme-accent text-black font-black shadow-sm'
                    : 'bg-zinc-800/60 text-zinc-400 hover:text-white'
                }`}
              >
                {labels[type]}
              </button>
            );
          })}
        </div>

        {/* Results List */}
        <div className="flex-1 overflow-y-auto p-2 divide-y divide-zinc-900 scrollbar-thin scrollbar-thumb-zinc-800">
          {searchResults.length === 0 ? (
            <div className="p-8 text-center">
              <Search className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-xs text-zinc-400 font-bold">Nenhum resultado para "{query}"</p>
              <p className="text-[11px] text-zinc-600 mt-1">
                Tente buscar por outro termo ou número de canal.
              </p>
            </div>
          ) : (
            searchResults.map((entry, index) => {
              const isSelected = index === selectedIndex;

              if (entry.type === 'live') {
                const c = entry.item;
                return (
                  <div
                    key={`ch-${c.id}`}
                    data-fav-id={c.id} // tecla KD (Stick): favorita o resultado selecionado
                    data-qs-selected={isSelected ? 'true' : undefined}
                    onClick={() => handleSelect(entry)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`p-2.5 rounded-2xl flex items-center justify-between gap-3 cursor-pointer transition ${
                      isSelected
                        ? 'bg-theme-accent-subtle border border-theme-accent-subtle text-white'
                        : 'hover:bg-zinc-900/60 text-zinc-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                        {c.logo ? (
                          <img
                            src={c.logo}
                            alt={c.name}
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-contain p-1"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=128';
                            }}
                          />
                        ) : (
                          <Tv className="w-5 h-5 text-theme-accent" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono font-bold text-theme-accent">
                            #{c.num}
                          </span>
                          <span className="text-xs font-bold text-white truncate">{c.name}</span>
                          <span className="text-[9px] px-1.5 py-0.2 rounded bg-theme-accent-subtle text-theme-accent-light font-bold">
                            AO VIVO
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-400 truncate">{c.groupTitle}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {isSelected && (
                        <span className="hidden sm:flex items-center gap-1 text-[10px] text-theme-accent font-bold">
                          <CornerDownLeft className="w-3 h-3" /> Abrir
                        </span>
                      )}
                      <Play className={`w-4 h-4 ${isSelected ? 'text-theme-accent fill-theme-accent' : 'text-zinc-600'}`} />
                    </div>
                  </div>
                );
              }

              if (entry.type === 'movie') {
                const m = entry.item;
                return (
                  <div
                    key={`mov-${m.id}`}
                    data-fav-id={m.id} // tecla KD (Stick): favorita o resultado selecionado
                    data-qs-selected={isSelected ? 'true' : undefined}
                    onClick={() => handleSelect(entry)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`p-2.5 rounded-2xl flex items-center justify-between gap-3 cursor-pointer transition ${
                      isSelected
                        ? 'bg-amber-500/15 border border-amber-500/40 text-white'
                        : 'hover:bg-zinc-900/60 text-zinc-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-12 rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden shrink-0">
                        <img src={m.poster} alt={m.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate">{m.title}</span>
                          <span className="text-[9px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-bold">
                            FILME
                          </span>
                          {m.quality && (
                            <span className="text-[9px] px-1 rounded bg-zinc-800 text-zinc-400 font-bold">
                              {m.quality}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400 truncate">
                          {m.category} • {m.year} • {m.duration} min
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {isSelected && (
                        <span className="hidden sm:flex items-center gap-1 text-[10px] text-amber-400 font-bold">
                          <CornerDownLeft className="w-3 h-3" /> Assistir
                        </span>
                      )}
                      <Play className={`w-4 h-4 ${isSelected ? 'text-amber-400 fill-amber-400' : 'text-zinc-600'}`} />
                    </div>
                  </div>
                );
              }

              if (entry.type === 'series') {
                const s = entry.item;
                return (
                  <div
                    key={`ser-${s.id}`}
                    data-fav-id={s.id} // tecla KD (Stick): favorita o resultado selecionado
                    data-qs-selected={isSelected ? 'true' : undefined}
                    onClick={() => handleSelect(entry)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`p-2.5 rounded-2xl flex items-center justify-between gap-3 cursor-pointer transition ${
                      isSelected
                        ? 'bg-purple-500/15 border border-purple-500/40 text-white'
                        : 'hover:bg-zinc-900/60 text-zinc-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-12 rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden shrink-0">
                        <SeriesPoster
                          poster={s.poster}
                          title={s.title}
                          category={s.category}
                          year={s.year}
                          size="sm"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate">{s.title}</span>
                          <span className="text-[9px] px-1.5 py-0.2 rounded bg-purple-500/20 text-purple-300 font-bold">
                            SÉRIE
                          </span>
                          <span className="text-[9px] px-1 rounded bg-zinc-800 text-zinc-400 font-bold">
                            {s.seasonsCount} Temp
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-400 truncate">
                          {s.category} • {s.episodes?.length || 0} Episódios
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {isSelected && (
                        <span className="hidden sm:flex items-center gap-1 text-[10px] text-purple-400 font-bold">
                          <CornerDownLeft className="w-3 h-3" /> Ver Episódios
                        </span>
                      )}
                      <Play className={`w-4 h-4 ${isSelected ? 'text-purple-400 fill-purple-400' : 'text-zinc-600'}`} />
                    </div>
                  </div>
                );
              }

              return null;
            })
          )}
        </div>

        {/* Footer Navigation Bar */}
        <div className="p-3 bg-zinc-950 border-t border-zinc-800/80 flex items-center justify-between text-[11px] text-zinc-500">
          <div className="flex items-center gap-4">
            <span>↑↓ Navegar</span>
            <span>↵ Abrir</span>
            <span>ESC Fechar</span>
          </div>
          <span className="font-mono">{searchResults.length} resultados</span>
        </div>
      </div>
    </div>
  );
};
